import Cookies from "js-cookie";
import axios from "axios";

import { DeleteFileBody } from "../api-hooks/images/useDeleteRecordFile";
import { DataTransforms } from "../../types/TenantInterfaces";
import { FileSources } from "../../types/FileInterfaces";
import FieldUtils from "../../utils/FieldUtils";
import Constants from "../../utils/Constants";
import API from "./API";

export function getTokenForSupportalSupportedEndpoints(
  tenantID: string | undefined
) {
  return tenantID
    ? Cookies.get("authenticationToken")
    : Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`);
}

export default class TenantAPI {
  static currentTenant = "";

  static setCurrentTenant(id: string) {
    this.currentTenant = id;
  }

  static getCurrentTenant() {
    return this.currentTenant;
  }

  static getAuthenticationToken(id: string) {
    const finalToken = Cookies.get("authenticationToken") || "undefined";

    return axios({
      method: "GET",
      url: window.GC_API_URL + `/tenant/token/${id}`,
      data: null,
      headers: {
        Authorization: "Bearer " + finalToken,
        "app-id": "GrowthCloud",
      },
      withCredentials: true,
    });
  }

  // ------------------------------------- Schema -------------------------------------
  static addDataTransforms(moduleAPIName: string, payload: DataTransforms) {
    return API._bearerRequest(
      "post",
      `/tenant/settings/crm/modules/${moduleAPIName}/dataTransforms`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static removeDataTransforms(moduleAPIName: string, id: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/crm/modules/${moduleAPIName}/dataTransforms/${id}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyDataTransforms(
    moduleAPIName: string,
    id: string,
    payload: DataTransforms
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/dataTransforms/${id}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getLatestSchema(supportTargetCloudID?: string) {
    const token = getTokenForSupportalSupportedEndpoints(supportTargetCloudID);

    return API._bearerRequest(
      "get",
      "/tenant/schema/latest" +
        `${supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""}`,
      null,
      token
    ).then((response) => {
      const schema = response.data.data.Schema;

      return schema;
    });
  }

  /**
   * Get the Group by options for a Field
   * @param {string | null} workspaceID
   * @param {string | null} fieldAPIName
   * @param {string | null} tenantID
   * @param {string | null} moduleAPIName
   */
  static getFieldGroups(
    workspaceID: string,
    fieldAPIName: string,
    moduleAPIName: string
  ) {
    return API._bearerRequest(
      "get",
      `/tenant/workspaces/${workspaceID}/pages/field-groups/${moduleAPIName}/${fieldAPIName}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------- Backup -------------------------------------
  static backupCloudNow() {
    return API._bearerRequest(
      "put",
      "/tenant/settings/backups/manual",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static backupCloudInterval(interval: any) {
    return API._bearerRequest(
      "put",
      "/tenant/settings/backup/tenants",
      { interval: interval },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getActiveBackups() {
    return API._bearerRequest(
      "get",
      "/tenant/settings/backups/automatic",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deactivateAutomaticBackup() {
    return API._bearerRequest(
      "delete",
      "/tenant/settings/backups/automatic",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getBackups(page: number, count: number, filter: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/backups?${
        !page || !count ? "" : `page=${page}&count=${count}`
      }`,
      filter,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static restoreBackup(tenantID: string, backupID: string) {
    return API._bearerRequest(
      "put",
      `/tenants/${tenantID}/restorebackup/${backupID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteBackup(backupID: string) {
    return API._bearerRequest(
      "delete",
      "/tenant/settings/backups/" + backupID,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------- Records -------------------------------------
  static searchRecords({
    moduleAPIName,
    query,
    tenantID,
  }: {
    moduleAPIName: string;
    query: any;
    tenantID?: string;
  }) {
    let payload = {
      Query: query,
    };
    const token = getTokenForSupportalSupportedEndpoints(tenantID);

    return API._bearerRequest(
      "put",
      "/tenant/crm/records/" +
        moduleAPIName +
        "/search" +
        (tenantID ? `?tenantID=${tenantID}` : ""),
      payload,
      token,
      tenantID
    );
  }

  static insertRecord(moduleAPIName: string, payload: Record<string, any>) {
    return API._bearerRequest(
      "post",
      "/tenant/crm/records/" + moduleAPIName,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static insertRecords(
    moduleAPIName: string,
    payload: any,
    supportTargetCloudID?: string
  ) {
    return API._bearerRequest(
      "post",
      `/tenant/crm/records/${moduleAPIName}/multiple${
        supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""
      }`,
      payload,
      supportTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyRecord = (
    moduleAPIName: string,
    id: string,
    payload: Record<string, any>
  ) => {
    return API._bearerRequest(
      "put",
      "/tenant/crm/records/" + moduleAPIName + "/" + id,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  };

  static modifyGlobalRecord(
    moduleAPIName: string,
    id: string,
    payload: any,
    tenantID?: string
  ) {
    const token = getTokenForSupportalSupportedEndpoints(tenantID);

    return API._bearerRequest(
      "put",
      "/tenant/crm/records/" +
        moduleAPIName +
        "/" +
        id +
        "/globalRecord" +
        (tenantID ? `?tenantID=${tenantID}` : ""),
      payload,
      token
    );
  }

  static bulkUpdateRecords(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/crm/records/${moduleAPIName}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateReferenceRecords(
    moduleAPIName: string,
    recordID: string,
    fieldAPIName: string,
    payload: string,
    subitemParent?: string,
    subitemParentID?: string
  ) {
    return API._bearerRequest(
      "put",
      "/tenant/crm/records/" +
        moduleAPIName +
        "/" +
        recordID +
        "/updateReferenceRecords/" +
        fieldAPIName,
      { payload, subitemParent, subitemParentID },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateMultipleText(
    moduleAPIName: string,
    fieldAPIName: string,
    recordID: string,
    payload: any,
    subitemParent?: string,
    subitemParentID?: string
  ) {
    return API._bearerRequest(
      "put",
      "/tenant/crm/records/" +
        moduleAPIName +
        "/" +
        recordID +
        "/updateMultipleText/" +
        fieldAPIName,
      { payload, subitemParent, subitemParentID },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static mergeRecords(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/crm/records/${moduleAPIName}/merge`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static lockRecord(
    moduleAPIName: string,
    id: string,
    type: "Lock" | "Unlock"
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/crm/records/${moduleAPIName}/${id}/lock`,
      { type },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getAllRecords(moduleAPIName: string) {
    return API._bearerRequest(
      "get",
      `/tenant/crm/records/${moduleAPIName}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ---------------------------- Subitem Related Endpoints ----------------------------
  static updateSubitemField(
    moduleAPIName: string,
    fieldAPIName: string,
    payload: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/fields/${fieldAPIName}/subitem`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateSubitemTableLayout(
    moduleAPIName: string,
    fieldAPIName: string,
    payload: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/fields/${fieldAPIName}/record-table-layout`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteSubitems(
    moduleAPIName: string,
    recordID: string,
    fieldAPIName: string,
    payload: any
  ) {
    return API._bearerRequest(
      "delete",
      "/tenant/crm/records/" +
        moduleAPIName +
        "/" +
        recordID +
        "/deleteSubitems/" +
        fieldAPIName,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getRecordByID(module: string, id: string, fields?: string[]) {
    return API._bearerRequest(
      "get",
      "/tenant/crm/records/" +
        module +
        "/" +
        id +
        `${fields ? `?fields=${JSON.stringify(fields)}` : ""}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getRecordByValue(
    moduleAPIName: string,
    fieldAPIName: string,
    fieldValue: string
  ) {
    return API._bearerRequest(
      "GET",
      `/tenant/crm/records/${moduleAPIName}/fields/${fieldAPIName}/${fieldValue}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static mergeSubitems(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/crm/records/${moduleAPIName}/merge/subitems`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getCSVTemplate(
    name: string,
    fileName: string,
    fields: string[] | undefined = []
  ) {
    let out = "";

    if (fields && fields.length) {
      out = fields.join(",");
    }

    return API._bearerRequest(
      "get",
      "/tenant/crm/records/" +
        name +
        "/templates/csv/" +
        fileName +
        "?fields=" +
        out,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // --------------------------------- File Management Endpoints ---------------------------------

  /***
   * Uploads a file to the tenant's file storage.
   * @param {File | Blob} file - The file to upload.
   * @param {boolean} usePublicContainer - Whether to use the public container.
   * @param {boolean} awaitIndex - Whether to await the indexing of the file.
   */
  static uploadFile({
    file,
    sources,
    usePublicContainer = false,
    awaitIndex,
  }: {
    file: string | Blob;
    sources: FileSources[] | undefined;
    usePublicContainer?: boolean;
    awaitIndex?: boolean;
  }) {
    let bodyFormData = new FormData();
    bodyFormData.append("File", file);

    if (usePublicContainer) {
      bodyFormData.append("isPublic", String(usePublicContainer));
    }

    sources && bodyFormData.append("sources", JSON.stringify(sources ?? []));

    if (awaitIndex) {
      bodyFormData.append("awaitIndex", String(awaitIndex));
    }

    return API._bearerRequest(
      "post",
      "/tenant/file",
      bodyFormData,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static indexFile({
    fileRecordID,
    recordID,
    fieldAPIName,
    moduleAPIName,
  }: {
    fileRecordID: string;
    recordID: string | undefined;
    fieldAPIName: string | undefined;
    moduleAPIName: string | undefined;
  }) {
    return API._bearerRequest(
      "post",
      "/tenant/file/index",
      { fileRecordID, recordID, fieldAPIName, moduleAPIName },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  /**
   * Delete files by blobName or fileID.
   */
  static deleteFiles(body: DeleteFileBody) {
    let { type, ...payload } = body;

    return API._bearerRequest(
      "DELETE",
      `/tenant/crm/records/files/delete/${body.type ?? "None"}`,
      {
        ...payload,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // For updating a File Record's Name, Caption, or Transcript.
  static updateFile(fileID: string, payload: any) {
    return API._bearerRequest(
      "PUT",
      `/tenant/file/${fileID}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // --------------------------------- CRM Endpoints ---------------------------------

  static startCSVImport(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "post",
      `/tenant/crm/records/${moduleAPIName}/import/file`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteRecord(moduleAPIName: string, id: string) {
    return API._bearerRequest(
      "delete",
      "/tenant/crm/records/" + moduleAPIName + "/" + id,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static bulkDeleteRecords(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "delete",
      "/tenant/crm/records/" + moduleAPIName,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getFileURL(
    fileName: string,
    expirationTimeSeconds = 3600,
    usePublicContainer = false,
    isSupportal = false,
    tenantID?: string
  ) {
    const path = `/tenant/file/url?file=${fileName}&exp=${expirationTimeSeconds}&isPublic=${usePublicContainer}${
      isSupportal ? `&tenantID=${tenantID}` : ""
    }`;

    return API._bearerRequest(
      "get",
      path,
      null,
      isSupportal
        ? getTokenForSupportalSupportedEndpoints(tenantID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getUsersFromTenant(tenantID?: string) {
    const endpoint = tenantID
      ? `/tenant/settings/users?tenantID=${tenantID}`
      : "/tenant/settings/users";

    const token = getTokenForSupportalSupportedEndpoints(tenantID);
    return API._bearerRequest("get", endpoint, null, token);
  }

  static updateUserTenantPermissions({
    userID,
    permissionID,
    supportTargetCloudID,
  }: {
    userID: string;
    permissionID: string;
    supportTargetCloudID: string | undefined;
  }) {
    const token = getTokenForSupportalSupportedEndpoints(supportTargetCloudID);

    return API._bearerRequest(
      "put",
      `/tenant/settings/users/${userID}/permissions/${permissionID}` +
        (supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""),
      null,
      token
    );
  }

  static inviteUser({
    userEmail,
    permissionProfile,
    workspaceInfo,
    supportTargetCloudID,
  }: {
    userEmail: string;
    permissionProfile: string;
    workspaceInfo: any;
    supportTargetCloudID: string | undefined;
  }) {
    const token = getTokenForSupportalSupportedEndpoints(supportTargetCloudID);

    return API._bearerRequest(
      "post",
      "/tenant/settings/users" +
        (supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""),
      {
        email: userEmail,
        permissionID: permissionProfile,
        ...(workspaceInfo && { workspaceInfo }),
      },
      token
    );
  }

  static resendUserInvite(userID: string) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/users/${userID}/resend`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static removeUserInvite({
    userID,
    supportTargetCloudID,
  }: {
    userID: string;
    supportTargetCloudID: string | undefined;
  }) {
    const token = getTokenForSupportalSupportedEndpoints(supportTargetCloudID);

    return API._bearerRequest(
      "delete",
      "/tenant/settings/users/" +
        userID +
        (supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""),
      null,
      token
    );
  }

  static assignCloudOwnership(personID: string, newPermissionProfile: string) {
    return API._bearerRequest(
      "put",
      "/tenant/settings/ownership",
      {
        PersonID: personID,
        NewPermissionProfileID: newPermissionProfile,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static addCRMModule(
    name: string,
    apiName: string,
    fields?: any,
    dynamicDataSource?: any,
    templates?: any
  ) {
    if (Constants.COSMOS_KEYWORDS.includes(apiName.toLowerCase())) {
      throw new Error("Invalid API Name");
    }

    return API._bearerRequest(
      "post",
      "/tenant/settings/crm/modules",
      {
        Name: name,
        APIName: apiName,
        Fields: fields, // optional - used in CreateModuleFromCSV, QueryBuilder features
        Templates: templates, //optional - used in CreateModuleFromCSV feature
        DynamicDataSource: dynamicDataSource, // optional used in QueryBuilder feature.
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyCRMModuleName(
    apiName: string,
    name: string,
    supportTargetCloudID?: string
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${apiName}?${
        supportTargetCloudID ? `tenantID=${supportTargetCloudID}` : ""
      }`,
      {
        Name: name,
      },
      supportTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyCRMDynamicModule(apiName: string, sql: any) {
    return API._bearerRequest(
      "put",
      "/tenant/settings/crm/modules/dynamic/" + apiName,
      {
        SQL: sql,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyModuleListConfiguration(apiName: string, dataSourceList: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${apiName}/list`,
      dataSourceList,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteModule(apiName: string, supportalTargetCloudID?: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/crm/modules/${apiName}${
        supportalTargetCloudID ? `?tenantID=${supportalTargetCloudID}` : ""
      }`,
      null,
      supportalTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportalTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createModuleFromCSV(payload: any) {
    return API._bearerRequest(
      "post",
      `/tools/createModuleFromCSV`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getFieldSchemaFromFieldValues(fieldValues: any) {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      fieldValues.defaultValue,
      fieldValues.fieldType
    );
    const type = fieldValues.fieldType;

    const includeSubitemRecordName =
      (type === "SingleReference" || type === "MultiReference") &&
      fieldValues.subitemRecordNameField;

    return {
      Name: fieldValues.name,
      APIName: fieldValues.apiName,
      Description: fieldValues.description ? fieldValues.description : "",
      FieldType: type,
      DefaultValue: formattedDefaultVal ?? null,
      ...((type === "SingleReference" || type === "MultiReference") &&
        fieldValues.defaultSource && {
          DefaultSource: fieldValues.defaultSource,
        }),
      ...(type === "SingleReference" && {
        Hierarchical: fieldValues.hierarchical ?? false,
      }),
      Required: fieldValues.required ? true : false,
      Unique: fieldValues.unique ? true : false,
      SourcesList: fieldValues.source ? fieldValues.source : null,
      ...(Constants.CALCULATED_FIELD_TYPES.includes(type) && {
        CalculatedFieldData: fieldValues.calculatedFieldData,
      }),
      ...(type === "Toggle" && {
        TrueDisplayName: fieldValues.trueDisplayName,
      }),
      ...(type === "Toggle" && {
        FalseDisplayName: fieldValues.falseDisplayName,
      }),
      ReadOnly: fieldValues.readonly,
      Sensitive: fieldValues.sensitive,
      RecordSummary: fieldValues.summary,
      RecordSummaryFields: fieldValues.summaryFields,
      ...(FieldUtils.fieldTypeIsSubitem(type) && {
        SubitemFields: fieldValues.subitemFields,
      }),
      ...(type === "Image" && { Presentation: fieldValues.presentation }),
      ...((type === "Image" ||
        type === "SingleFile" ||
        type === "Audio" ||
        type === "Video") && { Public: fieldValues.public }),
      ...(includeSubitemRecordName && {
        SubitemRecordNameField: fieldValues.subitemRecordNameField,
      }),
      ...(type === "URL" && { GcWebsite: fieldValues.gcWebsite }),
      Protected: fieldValues.protected ? true : false,
      RenameProtected: fieldValues.renameProtected ? true : false,
      ...((type === "SingleReference" || type === "MultiReference") && {
        GcUsers: fieldValues.gcUsers,
      }),
    };
  }

  /**
   * Create a module field.
   *
   */
  static createModuleField(
    moduleAPIName: string,
    fieldValues: any,
    supportTargetCloudID?: string
  ) {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      fieldValues.defaultValue,
      fieldValues.fieldType
    );
    const type = fieldValues.fieldType;

    const includeSubitemRecordName =
      (type === "SingleReference" || type === "MultiReference") &&
      fieldValues.subitemRecordNameField;

    const payload = {
      Name: fieldValues.name,
      APIName: fieldValues.apiName,
      Description: fieldValues.description ? fieldValues.description : "",
      FieldType: type,
      DefaultValue: formattedDefaultVal ?? null,
      ...((type === "SingleReference" || type === "MultiReference") &&
        fieldValues.defaultSource && {
          DefaultSource: fieldValues.defaultSource,
        }),
      ...(type === "SingleReference" && {
        Hierarchical: fieldValues.hierarchical ?? false,
      }),
      Required: fieldValues.required ? true : false,
      Unique: fieldValues.unique ? true : false,
      SourcesList: fieldValues.source ? fieldValues.source : null,
      ...(Constants.CALCULATED_FIELD_TYPES.includes(type) && {
        CalculatedFieldData: fieldValues.calculatedFieldData,
      }),
      ...(type === "Toggle" && {
        TrueDisplayName: fieldValues.trueDisplayName,
      }),
      ...(type === "Toggle" && {
        FalseDisplayName: fieldValues.falseDisplayName,
      }),
      ReadOnly: fieldValues.readonly,
      Sensitive: fieldValues.sensitive,
      RecordSummary: fieldValues.summary,
      RecordSummaryFields: fieldValues.summaryFields,
      ...(FieldUtils.fieldTypeIsSubitem(type) && {
        SubitemFields: fieldValues.subitemFields,
      }),
      ...(type === "Image" && { Presentation: fieldValues.presentation }),
      ...((type === "Image" ||
        type === "SingleFile" ||
        type === "Audio" ||
        type === "Video") && { Public: fieldValues.public }),
      ...(includeSubitemRecordName && {
        SubitemRecordNameField: fieldValues.subitemRecordNameField,
      }),
      ...(type === "URL" && { GcWebsite: fieldValues.gcWebsite }),
      Protected: fieldValues.protected ? true : false,
      RenameProtected: fieldValues.renameProtected ? true : false,
      ...((type === "SingleReference" || type === "MultiReference") && {
        GcUsers: fieldValues.gcUsers,
      }),
    };

    if (Constants.COSMOS_KEYWORDS.includes(fieldValues.apiName.toLowerCase())) {
      throw new Error("Invalid API Name");
    }

    return API._bearerRequest(
      "post",
      "/tenant/settings/crm/modules/" +
        moduleAPIName +
        "/fields" +
        `${supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""}`,
      payload,
      supportTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static editModuleField(
    moduleAPIName: string,
    fieldAPIName: string,
    fieldValues: any,
    supportTargetCloudID?: string
  ) {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      fieldValues.defaultValue,
      fieldValues.fieldType
    );
    const type = fieldValues.fieldType;

    const includeSubitemRecordName =
      (type === "Subitem" || type === "MultiSubitem") &&
      fieldValues.subitemRecordNameField;

    const payload = {
      Name: fieldValues.name,
      APIName: fieldAPIName,
      Description: fieldValues.description ? fieldValues.description : "",
      FieldType: type,
      DefaultValue: formattedDefaultVal,
      ...((type === "SingleReference" || type === "MultiReference") &&
        fieldValues.defaultSource && {
          DefaultSource: fieldValues.defaultSource,
        }),
      ...(type === "SingleReference" && {
        Hierarchical: fieldValues.hierarchical ?? false,
      }),
      Required: fieldValues.required ? true : false,
      Unique: fieldValues.unique ? true : false,
      SourcesList: fieldValues.source ? fieldValues.source : null,
      ...(type === "Toggle" && {
        TrueDisplayName: fieldValues.trueDisplayName,
      }),
      ...(type === "Toggle" && {
        FalseDisplayName: fieldValues.falseDisplayName,
      }),
      ...(Constants.CALCULATED_FIELD_TYPES.includes(type) && {
        CalculatedFieldData: fieldValues.calculatedFieldData,
      }),
      ReadOnly: fieldValues.readonly,
      Sensitive: fieldValues.sensitive,
      RecordSummary: fieldValues.summary,
      RecordSummaryFields: fieldValues.summaryFields,
      ...(FieldUtils.fieldTypeIsSubitem(type) && {
        SubitemFields: fieldValues.subitemFields,
      }),
      ...(type === "Image" && { Presentation: fieldValues.presentation }),
      ...(includeSubitemRecordName && {
        SubitemRecordNameField: fieldValues.subitemRecordNameField,
      }),
      ...((type === "Image" ||
        type === "SingleFile" ||
        type === "Audio" ||
        type === "Video") && { Public: fieldValues.public }),
      ...(type === "URL" && { GcWebsite: fieldValues.gcWebsite }),
      Protected: fieldValues.protected ? true : false,
      RenameProtected: fieldValues.renameProtected ? true : false,
      ...((type === "SingleReference" || type === "MultiReference") && {
        GcUsers: fieldValues.gcUsers,
      }),
    };

    if (Constants.COSMOS_KEYWORDS.includes(fieldAPIName.toLowerCase())) {
      throw new Error("Invalid API Name");
    }

    return API._bearerRequest(
      "put",
      "/tenant/settings/crm/modules/" +
        moduleAPIName +
        "/fields/" +
        fieldAPIName +
        `${supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""}`,
      payload,
      supportTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteModuleField(
    moduleAPIName: string,
    fieldAPIName: string,
    supportTargetCloudID?: string
  ) {
    return API._bearerRequest(
      "delete",
      "/tenant/settings/crm/modules/" +
        moduleAPIName +
        "/fields/" +
        fieldAPIName +
        `${supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""}`,
      null,
      supportTargetCloudID
        ? getTokenForSupportalSupportedEndpoints(supportTargetCloudID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ----------------------- Tiered Tags Endpoints -----------------------
  static getAllTags(workspaceID: string) {
    return API._bearerRequest(
      "get",
      `/tenant/tags?type=all&workspaceID=${workspaceID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getTags(scope: {
    Type: string;
    WorkspaceID?: string;
    ModuleAPIName?: string;
    PersonID?: string;
  }) {
    return API._bearerRequest(
      "get",
      `/tenant/tags?type=${scope.Type}${
        scope?.WorkspaceID ? `&workspaceID=${scope.WorkspaceID}` : ""
      }${scope?.ModuleAPIName ? `&moduleAPIName=${scope.ModuleAPIName}` : ""}${
        scope?.PersonID ? `&personID=${scope.PersonID}` : ""
      }`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ----------------------- Tag Creation Endpoints -----------------------
  static createCloudTag(payload: any) {
    return API._bearerRequest(
      "post",
      "/tenant/settings/tags",
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createWorkspaceTag(workspaceID: string, payload: any) {
    return API._bearerRequest(
      "post",
      `/tenant/workspaces/${workspaceID}/tags`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createModuleTag(moduleAPIName: string, payload: any) {
    return API._bearerRequest(
      "post",
      `/tenant/settings/crm/modules/${moduleAPIName}/tags`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createUserTag(personID: string, payload: any) {
    return API._bearerRequest(
      "post",
      `/tenant/settings/users/${personID}/tags`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ----------------------- Tag Update Endpoints -----------------------
  static updateCloudTag(tagID: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/tags/${tagID}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateWorkspaceTag(workspaceID: string, tagID: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/workspaces/${workspaceID}/tags/${tagID}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateModuleTag(moduleAPIName: string, tagID: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/tags/${tagID}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateUserTag(personID: string, tagID: string, payload: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/users/${personID}/tags/${tagID}`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ----------------------- Tag Deletion Endpoints -----------------------
  static deleteCloudTag(tagID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/tags/${tagID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteWorkspaceTag(workspaceID: string, tagID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/workspaces/${workspaceID}/tags/${tagID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteModuleTag(moduleAPIName: string, tagID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/crm/modules/${moduleAPIName}/tags/${tagID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteUserTag(personID: string, tagID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/users/${personID}/tags/${tagID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ----------------------- Tag Group Endpoints -----------------------
  static updateTagGroup({
    groupName,
    payload,
    scope,
  }: {
    groupName: string;
    payload: any;
    scope: any;
  }) {
    return API._bearerRequest(
      "put",
      `/tenant/tags/groups/${groupName}?type=${scope.Type}${
        scope?.WorkspaceID ? `&workspaceID=${scope.WorkspaceID}` : ""
      }${scope?.ModuleAPIName ? `&moduleAPIName=${scope.ModuleAPIName}` : ""}${
        scope?.PersonID ? `&personID=${scope.PersonID}` : ""
      }`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteTagGroup({
    groupName,
    scope,
  }: {
    groupName: string;
    scope: any;
  }) {
    return API._bearerRequest(
      "delete",
      `/tenant/tags/groups/${groupName}?type=${scope.Type}${
        scope?.WorkspaceID ? `&workspaceID=${scope.WorkspaceID}` : ""
      }${scope?.ModuleAPIName ? `&moduleAPIName=${scope.ModuleAPIName}` : ""}${
        scope?.PersonID ? `&personID=${scope.PersonID}` : ""
      }`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateRecordTags(
    moduleAPIName: string,
    recordID: string,
    payload: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/crm/records/${moduleAPIName}/${recordID}/tags`,
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------- Module Template Management -------------------
  static modifyRecordNameTemplate(
    moduleAPIName: string,
    recordNameTemplate: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/record-name-template`,
      { recordNameTemplate },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyRecordNameImage(moduleAPIName: string, recordNameImage: any) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/record-name-image`,
      { recordNameImage },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyRecordNameMetadata(
    moduleAPIName: string,
    recordNameMetadata: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/record-name-metadata`,
      { recordNameMetadata },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyQuickSearchFields(
    moduleAPIName: string,
    quickSearchFields: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/record-quick-search`,
      quickSearchFields,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static modifyLayoutSections(
    moduleAPIName: string,
    layoutSections: any,
    dynamicLayoutField: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/record-detail-layout`,
      { layout: layoutSections, dynamicLayoutField: dynamicLayoutField },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static addCredential(name: string) {
    return API._bearerRequest(
      "post",
      "/tenant/settings/credentials",
      {
        Name: name ? name : "API Key",
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteCredential(credentialID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/settings/credentials/${credentialID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static changeAPIKey(credentialID: string) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/credentials/rotate/${credentialID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getCredentialsForTenant() {
    return API._bearerRequest(
      "get",
      "/tenant/settings/credentials",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // --------------------------- Permission Profiles -------------------------------------
  static getPermissionProfiles(supportTargetCloudID?: string) {
    return API._bearerRequest(
      "get",
      "/tenant/settings/permissions" +
        (supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""),
      null,
      !supportTargetCloudID
        ? Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
        : Cookies.get("authenticationToken")
    );
  }

  // ------------------------------------ Export Data -------------------------------------------
  static exportData(
    FileName: string,
    Query: any,
    Records: any,
    moduleAPIName: string,
    FileType: any,
    MultiSubitemField: any,
    ParentRecordID: any,
    GrowthCloudExport: any,
    IsSQLQuery = false
  ) {
    return API._bearerRequest(
      "post",
      "/tenant/crm/records/" + moduleAPIName + "/export/csv",
      {
        FileName,
        Query,
        Records,
        FileType,
        MultiSubitemField,
        ParentRecordID,
        GrowthCloudExport,
        IsSQLQuery,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static exportModuleJson(moduleAPIName: string, tenantID?: string) {
    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/export${
        tenantID ? `?tenantID=${tenantID}` : ""
      }`,
      null,
      tenantID
        ? getTokenForSupportalSupportedEndpoints(tenantID)
        : Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static importModuleJson(file: any) {
    return API._bearerRequest(
      "put",
      `/settings/crm/modules/file/import`,
      { file: file },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ User Activity -------------------------------------------
  static setUserActivity({ WorkspaceID, Activity }: any) {
    return API._bearerRequest(
      "post",
      "/people/activities",
      { WorkspaceID, Activity },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getAllUsersActivity(page: number = 1, limit: number = 25) {
    return API._bearerRequest(
      "get",
      `/people/activities?page=${page}&limit=${limit}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getUserActivity(userID: string) {
    return API._bearerRequest(
      "get",
      `/people/activities/${userID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ Quick Creates -------------------------------------------
  static getQuickCreates() {
    return API._bearerRequest(
      "get",
      "/tenant/quickcreates/getAll",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createQuickCreate({ prefill }: { prefill: any }) {
    return API._bearerRequest(
      "post",
      "/tenant/quickcreates",
      {
        prefill,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateQuickCreate(updatedQuickCreate: any) {
    return API._bearerRequest(
      "put",
      `/tenant/quickcreates/${updatedQuickCreate.id}`,
      { updatedQuickCreate },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteQuickCreate(prefillID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/quickcreates/${prefillID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ Public Forms -------------------------------------------
  static getPublicFormByID(formID: string, tenantID: string) {
    return API._bearerRequest(
      "get",
      `/tenant/publicForms/get/${formID}/${tenantID}`,
      null
    );
  }

  static getPublicForms() {
    return API._bearerRequest(
      "get",
      "/tenant/publicForms/getAll",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createPublicForm({ form }: { form: any }) {
    return API._bearerRequest(
      "post",
      "/tenant/publicForms",
      {
        form,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updatePublicForm(updatedForm: any) {
    return API._bearerRequest(
      "put",
      `/tenant/publicForms/${updatedForm._id}`,
      { updatedForm },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deletePublicForm(formID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/publicForms/${formID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createRecordWithForm(
    formID: string,
    tenantID: string,
    payload: any,
    userID: string
  ) {
    return API._bearerRequest(
      "post",
      `/tenant/publicForms/createRecord/${formID}/${tenantID}${
        userID ? `?userID=${userID}` : ""
      }`,
      payload
    );
  }

  static searchRecordsWithForm({
    formID,
    moduleAPIName,
    query,
    tenantID,
  }: {
    formID: string;
    moduleAPIName: string;
    query: any;
    tenantID: string;
  }) {
    return API._bearerRequest(
      "put",
      `/tenant/publicForms/search/${formID}/${tenantID}/${moduleAPIName}`,
      {
        Query: query,
      }
    );
  }

  static uploadFileInForm({
    formID,
    tenantID,
    file,
    sources,
  }: {
    formID: string;
    tenantID: string;
    file: any;
    sources: FileSources[] | undefined;
  }) {
    let bodyFormData = new FormData();
    bodyFormData.append("File", file);
    bodyFormData.append("Sources", JSON.stringify(sources));

    return API._bearerRequest(
      "post",
      `/tenant/publicForms/file/${formID}/${tenantID}`,
      bodyFormData
    );
  }

  static getPublicFormsFileURL({
    formID,
    tenantID,
    expirationTimeSeconds,
    fileName,
  }: {
    formID: string;
    tenantID: string;
    expirationTimeSeconds: number;
    fileName: string;
  }) {
    return API._bearerRequest(
      "get",
      `/tenant/publicForms/file/${formID}/${tenantID}?file=${fileName}&exp=${expirationTimeSeconds}&tenantID=${tenantID}`
    );
  }

  // ------------------------------------ Global QuickCreate and Layouts endpoints -------------------------------------------
  static getGlobals(moduleAPIName: "QuickCreates" | "Layouts") {
    return API._bearerRequest(
      "get",
      `/tenant/globals/${moduleAPIName}/getAll`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static createGlobal(moduleAPIName: "QuickCreates" | "Layouts", global: any) {
    return API._bearerRequest(
      "post",
      `/tenant/globals/${moduleAPIName}`,
      {
        global,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static updateGlobal(
    moduleAPIName: "QuickCreates" | "Layouts",
    updatedGlobal: any
  ) {
    return API._bearerRequest(
      "put",
      `/tenant/globals/${moduleAPIName}/${updatedGlobal.id}`,
      { updatedGlobal },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteGlobal(
    moduleAPIName: "QuickCreates" | "Layouts",
    globalID: any
  ) {
    return API._bearerRequest(
      "delete",
      `/tenant/globals/${moduleAPIName}/${globalID}`,
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static getIndexes(moduleAPIName: string, supportalTargetCloudID: string) {
    const token = getTokenForSupportalSupportedEndpoints(
      supportalTargetCloudID
    );

    return API._bearerRequest(
      "get",
      `/tenant/settings/crm/modules/${moduleAPIName}/indexes${
        supportalTargetCloudID ? `?tenantID=${supportalTargetCloudID}` : ""
      }`,
      null,
      token
    );
  }

  static crudIndex(
    moduleAPIName: string,
    supportTargetCloudID: string,
    data: any
  ) {
    const token = getTokenForSupportalSupportedEndpoints(supportTargetCloudID);

    return API._bearerRequest(
      "put",
      `/tenant/settings/crm/modules/${moduleAPIName}/indexes${
        supportTargetCloudID ? `?tenantID=${supportTargetCloudID}` : ""
      }`,
      data,
      token
    );
  }

  static getBulkEmails() {
    return API._bearerRequest(
      "get",
      "/tools/bulkEmail",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static crudBulkEmail(payload: any) {
    return API._bearerRequest(
      "put",
      "/tools/crudBulkEmail",
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static sendBulkEmail(payload: any) {
    return API._bearerRequest(
      "post",
      "/tools/bulk/email",
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ Query Builder endpoints -------------------------------------------
  static queryBuilder(
    sqlQuery: any,
    alreadyTransformedQuery = false,
    gcQuery: any
  ) {
    return API._bearerRequest(
      "put",
      "/tenant/settings/crm/modules/query-builder",
      {
        sqlQuery,
        alreadyTransformedQuery,
        gcQuery,
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  static deleteQueryBuilderView() {
    return API._bearerRequest(
      "put",
      "/tenant/settings/crm/modules/query-builder",
      {
        Type: "delete",
      },
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ Deleted Records endpoints -------------------------------------------
  static deletedRecordOperations(payload: any) {
    return API._bearerRequest(
      "put",
      "/tenant/crm/records/",
      payload,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }

  // ------------------------------------ Orphan Collections endpoints -------------------------------------------

  static getOrphanCollections(
    pagination: { Page: number; Count: number },
    tenantID?: string
  ) {
    return API._bearerRequest(
      "put",
      `/mc-tools/orphan/collections${tenantID ? `?tenantID=${tenantID}` : ""}`,
      {
        Pagination: pagination,
      },
      getTokenForSupportalSupportedEndpoints(tenantID)
    );
  }

  static deleteOrphanCollection(collectionName: string, tenantID?: string) {
    return API._bearerRequest(
      "delete",
      `/mc-tools/orphan/collections/${collectionName}${
        tenantID ? `?tenantID=${tenantID}` : ""
      }`,
      null,
      getTokenForSupportalSupportedEndpoints(tenantID)
    );
  }

  // ------------------------------------ Cloud Search Index --------------------------------------------
  static async updateCloudFileChunkIndex(tenantID: string) {
    return API._bearerRequest(
      "put",
      "/tenants/" + tenantID + "/create-file-chunk-index",
      null,
      Cookies.get(`authenticationToken_${this.getCurrentTenant()}`)
    );
  }
}
